import { Form, Select } from 'antd';
import React, { FC, Fragment, useEffect, useMemo, useState } from 'react';

import { JSELECT_CUSTOM_VALUE, TRANSLATION_CODES } from '../../constants';
import { useCompositorContext } from '../CompositorProvider';
import { JValueSelectProps } from './../../models';
import { FormItem } from './../FormItem';
import { JText } from './JText';
import {FieldDTOTypes} from "../../models/overrideBE";


export const JValueSelect: FC<JValueSelectProps> = (props) => {
    // Generate depends name on same current select level
    const dependsOnFullname = useMemo(() => {
        if (props.dependsOn) {
            const index = props.fullname.indexOf(props.fieldKey);
            const namepath = props.fullname.slice()
            if (index !== -1) {
                namepath[index] = props.dependsOn
                return namepath;
            }
        }
        return ['']
    }, [props.dependsOn])

    const formInstance = Form.useFormInstance()
    const { translate } = useCompositorContext()
    const [showInputText, setShowInputText] = useState<boolean>()
    const dependsValue = Form.useWatch<string | string[]>(dependsOnFullname, formInstance)
    const currentValue = Form.useWatch<string | string[]>(props.fullname, formInstance)
    const hasCustomOption = useMemo(() => !props.mode && !!props.options.find(o => o.value === JSELECT_CUSTOM_VALUE), [props.options])

    useEffect(() => {

        // Check if value is a custom value
        if (hasCustomOption && props.value && !props.options.find(o => o.value === currentValue)) {
            setShowInputText(true)
        }
    }, [])

    useEffect(() => {
        // Check selected value dependencies
        if (props.dependsOn && currentValue !== undefined) {
            const currentValues = Array.isArray(currentValue) ? currentValue : [currentValue];
            const selectedOptions = options.filter(o => currentValues.includes(o.props.value));
            const nextValues: string[] = selectedOptions.filter(filterOptions).map(o => o.props.value);
            if (nextValues.length !== currentValues.length) {
                formInstance.setFieldValue(props.fullname, nextValues.length ? nextValues : undefined);
            }
        }
    }, [dependsValue])

    const options: JSX.Element[] = useMemo(() => {
        return props.options?.map((option, i) => {
            return (
                <Select.Option key={i} value={option.value} dependsValue={option.dependsValue}>
                    {
                        option.svg && <div className='select-option-svg-icon' dangerouslySetInnerHTML={{ __html: option.svg }}></div>
                    }
                    {option.label}
                </Select.Option>
            )
        }) || []
    }, [props.options])

    // Filter select options based on deps
    const filterOptions = (option: JSX.Element) => {
        if (!props.dependsOn || !option.props.dependsValue) return true
        const values = Array.isArray(dependsValue) ? dependsValue : [dependsValue]
        return values.includes(option.props.dependsValue)
    }


    // Define if display input text if the custom value is selected
    useEffect(() => {
        if (currentValue === JSELECT_CUSTOM_VALUE) {
            setShowInputText(true)
            formInstance.setFieldValue(props.fullname, undefined);
        } else if (props.options.find(o => o.value === currentValue) && showInputText) {
            setShowInputText(false)
        }
    }, [currentValue])

    const value = useMemo(() => {
        if (hasCustomOption && !props.mode) {
            return props.options.find(o => o.value === (showInputText ? JSELECT_CUSTOM_VALUE : currentValue))
        }
        return undefined
    }, [hasCustomOption, props.mode, props.options, showInputText])

    const notFoundContentLabel = useMemo(() => {
        const label = translate(TRANSLATION_CODES.NOT_FOUND_CONTENT)
        if (label === TRANSLATION_CODES.NOT_FOUND_CONTENT) return 'No data'
        return label
    }, [])

    return (
        <Fragment>
            <FormItem
                fieldProps={{
                    ...props,
                    mandatory: showInputText ? false : props.mandatory,
                    value: props.type === FieldDTOTypes.JCOUNTRY || props.options.find(o => o.value === props.value) ? props.value : undefined // checking if the default value is among the options values. If not, defaultValue iset to undefined
                }}
            >
                <Select
                    onClear={() => setShowInputText(false)}
                    allowClear={!props.disableAllowClear}
                    value={value}
                    showSearch={props.searcheable}
                    notFoundContent={notFoundContentLabel}
                    placeholder={props.placeholder}
                    mode={props.mode}
                    disabled={props.disabled}
                    aria-describedby={props.validationMessage}
                >
                    {
                        options.filter(filterOptions)
                    }
                </Select>
            </FormItem>
            {
                showInputText && (
                    <JText {...props} showLabel={false} value={currentValue}></JText>
                )
            }
        </Fragment>
    )
}

